import { CommonModalDweb, FormattedText, Spinner } from "@tm/static";
import React, { useCallback, useEffect, useRef, useState } from "react";

import { fetchInvoiceList } from "@tm/insurance/api";
import { DOWNLOAD_INVOICE_STRINGS } from "../../constants";
import type { InvoiceDetail, InvoiceListResponse } from "../../types";
import { savePdfFromBytes } from "../../utils/savePdfFromBytes";
import {
  Container,
  DownloadLabel,
  EmptyWrapper,
  ErrorSubtitle,
  ErrorTitle,
  ErrorWrapper,
  InvoiceContent,
  InvoiceDate,
  InvoiceRow,
  InvoiceTitle,
  LoaderWrapper,
  RetryButton,
} from "./DownloadInvoiceModal.style";

interface DownloadInvoiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  invoiceUrl: string;
  bookingId: string;
}

export const DownloadInvoiceModal: React.FC<DownloadInvoiceModalProps> = ({
  isOpen,
  onClose,
  invoiceUrl,
  bookingId,
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [invoiceData, setInvoiceData] = useState<InvoiceListResponse | null>(
    null
  );
  const hasFetched = useRef(false);

  const callInvoiceApi = useCallback(async () => {
    if (!invoiceUrl || !bookingId) return;
    setLoading(true);
    setError(false);
    setInvoiceData(null);
    try {
      const response = await fetchInvoiceList(invoiceUrl, bookingId);
      const hasInvoiceList = Array.isArray(response?.invoiceDetails);

      if (!hasInvoiceList && (response?.error || response?.errorPageData)) {
        setError(true);
      } else {
        setInvoiceData(response);
      }
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [invoiceUrl, bookingId]);

  useEffect(() => {
    if (isOpen && !hasFetched.current) {
      hasFetched.current = true;
      callInvoiceApi();
    }
    if (!isOpen) {
      hasFetched.current = false;
      setLoading(true);
      setError(false);
      setInvoiceData(null);
    }
  }, [isOpen, callInvoiceApi]);

  const handleDownload = useCallback((item: InvoiceDetail) => {
    const fileName = `${
      item?.invoiceType ?? DOWNLOAD_INVOICE_STRINGS.INVOICE_FALLBACK
    }_${item?.invoiceno ?? ""}.pdf`;
    try {
      savePdfFromBytes(item.bytes, fileName);
    } catch {
      // silently fail — browser will show default download error
    }
  }, []);

  if (!isOpen) return null;

  const visibleInvoices =
    invoiceData?.invoiceDetails?.filter((item) => item.isVisible !== false) ??
    [];
  const isEmpty = !loading && !error && visibleInvoices.length === 0;

  return (
    <CommonModalDweb
      handleClick={onClose}
      customTitle={DOWNLOAD_INVOICE_STRINGS.MODAL_TITLE}
      customWidth="420px"
    >
      <Container>
        {loading && (
          <LoaderWrapper>
            <Spinner spinnerWidth={28} thickness={3} />
          </LoaderWrapper>
        )}

        {!loading && error && (
          <ErrorWrapper>
            <ErrorTitle>
              <FormattedText value={DOWNLOAD_INVOICE_STRINGS.ERROR_TITLE} />
            </ErrorTitle>
            <ErrorSubtitle>
              <FormattedText value={DOWNLOAD_INVOICE_STRINGS.ERROR_SUBTITLE} />
            </ErrorSubtitle>
            <RetryButton onClick={callInvoiceApi}>
              <FormattedText value={DOWNLOAD_INVOICE_STRINGS.RETRY} />
            </RetryButton>
          </ErrorWrapper>
        )}

        {!loading && !error && isEmpty && (
          <EmptyWrapper>
            <FormattedText value={DOWNLOAD_INVOICE_STRINGS.EMPTY_STATE} />
          </EmptyWrapper>
        )}

        {!loading &&
          !error &&
          visibleInvoices.map((item) => (
            <InvoiceRow
              key={item.invoiceno}
              onClick={() => handleDownload(item)}
            >
              <InvoiceContent>
                <InvoiceTitle>
                  <FormattedText value={item.title ?? ""} />
                </InvoiceTitle>
                {!!item.invoiceDateFormatted && (
                  <InvoiceDate>
                    <FormattedText value={item.invoiceDateFormatted} />
                  </InvoiceDate>
                )}
              </InvoiceContent>
              <DownloadLabel>
                <FormattedText value={DOWNLOAD_INVOICE_STRINGS.DOWNLOAD} />
              </DownloadLabel>
            </InvoiceRow>
          ))}
      </Container>
    </CommonModalDweb>
  );
};
